// Portfolio content (mock data)
export const profile = {
  name: "Meghashyam Adimallam",
  shortName: "Meghashyam",
  title: "AI/ML Engineer",
  subtitle: "LLM Systems · RAG Pipelines · Inference Optimization",
  experienceLine: "3+ years building production AI",
  availability: "Open to Remote Opportunities & Relocation",
  location: "Birmingham, AL · Open to Remote & Relocation",
  email: "meghashyamadimallam311@gmail.com",
  linkedin: "https://www.linkedin.com/in/meghashyam-adimallam-931327264/",
  github: "https://github.com/Meghashyam-adimallam",
  website: "https://meghashyamadimallam.com/",
  resumeUrl: "/Meghashyam_Resume.pdf",
  photoUrl: "/profile-photo.jpg",
  hero: {
    intro:
      "AI/ML Engineer with 3+ years of experience building scalable AI systems focused on LLM inference, Retrieval-Augmented Generation, multi-agent workflows, and real-time ML deployment.",
    stack:
      "Python · PyTorch · FastAPI · LangChain · vLLM · TensorRT-LLM · AWS",
  },
  about: [
    "AI/ML Engineer with 3+ years of experience and a Master's graduate in Data Science from the University of Alabama at Birmingham, building scalable AI systems across Machine Learning, NLP, Generative AI, and real-time inference engineering.",
    "I work on production-ready ML and LLM applications — Retrieval-Augmented Generation systems, NLP automation pipelines, real-time inference APIs, and computer vision services — using Python, PyTorch, FastAPI, LangChain, Hugging Face Transformers, OpenCV, AWS, Docker, and Kubernetes.",
    "Professionally, I've shipped NLP classification systems with BERT and SpaCy, predictive maintenance models with scikit-learn and XGBoost, and optimized LLM inference pipelines with vLLM for high-throughput GPU-backed serving. I enjoy the engineering side: scalability, latency, throughput, and reliability under load.",
    "My focus areas are Generative AI, LLM inference optimization, AI agents, RAG architectures, and MLOps. I care about building efficient, reliable, measurable AI systems — not demos.",
    "Graduated with a Master's in Data Science from UAB in May 2026 and actively working in AI/ML Engineering — open to remote opportunities and relocation for roles in Generative AI, Applied Machine Learning, LLM Systems, and MLOps where I can ship real systems and grow alongside strong engineers and researchers.",
  ],
};

export const navItems = [
  { label: "About", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Stack", href: "#stack" },
  { label: "Contact", href: "#contact" },
];

export const experiences = [
  {
    role: "AI/ML Engineer",
    company: "Current",
    location: "Remote",
    period: "May 2025 — Present",
    bullets: [
      "Built scalable RAG systems using LangChain, OpenAI GPT-4, and vector retrieval pipelines to improve document search and summarization.",
      "Optimized real-time LLM inference pipelines using vLLM for low-latency, high-throughput AI workloads.",
      "Developed and deployed FastAPI-based ML/LLM APIs with Docker and AWS ECS for scalable production inference.",
      "Fine-tuned BERT models using Hugging Face Transformers to improve NLP classification accuracy and inference efficiency.",
    ],
    stack: ["vLLM", "LLMs", "FastAPI", "LangChain", "AWS", "Docker", "Hugging Face Transformers", "Python", "PyTorch"],
    accent: "cyan",
  },
  {
    role: "Machine Learning Engineer",
    company: "Gridlogic",
    location: "India",
    period: "Jan 2023 — Jul 2024",
    bullets: [
      "Developed ML systems for predictive maintenance and anomaly detection using supervised and unsupervised learning.",
      "Built real-time computer vision pipelines with OpenCV and YOLO for automated defect detection.",
      "Designed scalable NLP preprocessing and feature-engineering pipelines for large-scale operational datasets.",
      "Deployed ML services on AWS (EC2, Lambda) for scalable real-time inference.",
    ],
    stack: ["Python", "Scikit-learn", "OpenCV", "YOLO", "AWS", "NLP"],
    accent: "teal",
  },
  {
    role: "AI/ML Engineer Trainee",
    company: "Pegasystems",
    location: "India",
    period: "Jun 2022 — Dec 2022",
    bullets: [
      "Built NLP text-classification pipelines with BERT and SpaCy for internal automation workflows.",
      "Implemented scalable data preprocessing and ML pipelines using MLflow and Kubeflow.",
      "Supported model training, deployment, and experiment tracking workflows for production ML systems.",
    ],
    stack: ["Python", "BERT", "SpaCy", "MLflow", "Kubeflow"],
    accent: "coral",
  },
];

export const projects = [
  {
    id: "inference",
    index: "01",
    category: "Systems · Inference",
    accent: "cyan",
    title: "Systems-Level Optimization of LLM Inference on a Single Consumer GPU",
    description:
      "A systems-focused LLM inference pipeline on a single NVIDIA RTX 4060. Async dynamic batching, KV-cache optimization, INT8/FP16 quantization, and a FastAPI serving layer engineered for throughput and latency.",
    highlights: [
      "Async dynamic batching",
      "KV cache optimization",
      "INT8/FP16 quantization",
      "FastAPI inference serving",
    ],
    metrics: [
      { label: "Throughput", value: "3×", caption: "improvement" },
      { label: "Latency", value: "−74%", caption: "p50 latency" },
      { label: "GPU Util.", value: "25→75%", caption: "utilization" },
      { label: "Cost / 1K req", value: "−69%", caption: "reduction" },
    ],
    stack: ["Python", "PyTorch", "CUDA", "FastAPI", "Hugging Face", "Quantization"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/567d72b0-6a32-4256-8c91-8278a4e7aa73/images/19f47431683496d9f48c53f22d68529ded8abc91df3327ddd3ee348252f06663.png",
    github:
      "https://github.com/Meghashyam-adimallam/Systems-Level-Optimization-of-LLM-Inference-on-a-Single-Consumer-GPU",
    caseStudy: {
      tagline:
        "Squeezing production-grade throughput out of a single RTX 4060 — a systems study in batching, caching, and quantization.",
      role: "Sole engineer · 6 weeks",
      year: "2025",
      problem:
        "Most 'LLM inference servers' assume cluster-class GPUs. The goal here was inverted: take a single consumer GPU (RTX 4060, 8 GB VRAM) and make it serve real workloads at production latencies. Naïve serving gave low GPU utilization, head-of-line blocking, and unstable p99 — the same pattern that hurts cost-per-request at any scale.",
      architecture: [
        { node: "FastAPI", note: "Async request gateway" },
        { node: "Batch Queue", note: "Token-bucket dynamic batching" },
        { node: "Quant Engine", note: "INT8 / FP16 weights" },
        { node: "KV Cache", note: "Paged + reused per session" },
        { node: "RTX 4060", note: "CUDA inference kernel" },
        { node: "Response Stream", note: "SSE token-by-token" },
      ],
      decisions: [
        {
          title: "Async dynamic batching over static batch size",
          body: "A static batch wastes the GPU when requests arrive sparsely. I implemented a token-bucket batcher that opens a short window (10–30 ms), groups arrivals, and fires a single forward pass — keeping the GPU saturated without starving long-running requests.",
        },
        {
          title: "INT8 weights + FP16 activations",
          body: "Pure INT8 hurt quality on long-form generation. Mixed precision — INT8 weights with FP16 activations — gave a clean 1.9× memory reduction while keeping eval quality within margin of error on internal NLI tasks.",
        },
        {
          title: "Paged KV cache with session reuse",
          body: "Tokens in a session share prefixes; recomputing them is pure waste. A paged KV cache (block-aligned) cut redundant compute on multi-turn chats and unblocked longer context windows on the same VRAM budget.",
        },
        {
          title: "FastAPI over a thin Triton wrapper",
          body: "Triton was overkill for a single GPU. A FastAPI service + asyncio worker pool gave equivalent throughput, faster iteration, and direct control over batching policy.",
        },
      ],
      results: [
        "Sustained 3× tokens/sec at p50 under load",
        "Tail latency (p99) reduced by 74%",
        "GPU utilization climbed from ~25% to ~75%",
        "Cost per 1K requests dropped 69% vs. the naïve baseline",
      ],
      lessons:
        "The biggest wins came from queuing theory, not the model. Batching policy and KV reuse moved metrics more than any kernel change. For consumer GPUs, memory pressure — not FLOPs — is the real ceiling.",
      whatNext:
        "Port the batching policy to a multi-GPU setup with tensor parallelism, and experiment with speculative decoding on top of the existing KV cache layer.",
    },
  },
  {
    id: "rag",
    index: "02",
    category: "RAG · Multi-Agent",
    accent: "teal",
    title: "Multi-Agent Verified RAG System for Hallucination Reduction",
    description:
      "Multi-agent RAG architecture combining hybrid retrieval and verification workflows. BM25 + dense retrieval, NLI verification agent, orchestration layer, and an evaluation backend with automated comparative testing.",
    highlights: [
      "BM25 + dense retrieval",
      "NLI verification agent",
      "Multi-agent orchestration",
      "Automated evaluation harness",
    ],
    metrics: [
      { label: "Hallucination", value: "50→0%", caption: "evaluated claims" },
      { label: "Retrieval", value: "Hybrid", caption: "BM25 + dense" },
      { label: "Verification", value: "NLI", caption: "agent loop" },
    ],
    stack: ["LangChain", "FAISS", "FastAPI", "PyTorch", "NLP"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/567d72b0-6a32-4256-8c91-8278a4e7aa73/images/8a60de8e4f4e98caacf0d078d5324b7ebbdf3507193a7dbfd18c499cda725d77.png",
    github:
      "https://github.com/Meghashyam-adimallam/A-Multi-Agent-Grounded-System-for-Hallucination-Reduction-and-Measurable-LLM-Evaluation",
    caseStudy: {
      tagline:
        "If a RAG answer can't be defended by retrieved evidence, it shouldn't leave the system. Verification as a first-class agent.",
      role: "Sole engineer · 8 weeks",
      year: "2025",
      problem:
        "Standard RAG hallucinates confidently when retrieval is weak or partially relevant. The goal: a measurable architecture that catches hallucinations before they reach the user — and proves it with an evaluation harness, not vibes.",
      architecture: [
        { node: "Query", note: "User question" },
        { node: "Retriever (Hybrid)", note: "BM25 + dense (FAISS)" },
        { node: "Drafter Agent", note: "LLM proposes answer + citations" },
        { node: "Verifier Agent", note: "NLI entailment over evidence" },
        { node: "Orchestrator", note: "Routes / retries / rejects" },
        { node: "Eval Backend", note: "Tracks claim-level scores" },
      ],
      decisions: [
        {
          title: "Hybrid retrieval (BM25 + dense)",
          body: "Pure dense retrieval misses exact-match clauses; pure BM25 misses semantic neighbors. A weighted fusion (RRF) gave consistently better recall@k on the eval set, which directly raised the verifier's acceptance rate.",
        },
        {
          title: "NLI as the verification signal",
          body: "Instead of asking an LLM 'is this true?', the verifier runs NLI between each generated claim and the retrieved evidence. Entailment / contradiction / neutral becomes a cheap, deterministic check — the orchestrator can act on it.",
        },
        {
          title: "Agent orchestration over chained prompts",
          body: "A graph of agents (drafter → verifier → optional re-retriever) is far more debuggable than a long prompt chain. Each edge is a measurable event with its own latency and pass-rate.",
        },
        {
          title: "Automated comparative harness",
          body: "I built an eval harness that runs vanilla-RAG and verified-RAG on the same claims and reports per-claim faithfulness — so the win is reproducible, not anecdotal.",
        },
      ],
      results: [
        "Hallucination rate on the eval set: 50% → 0% across measured claims",
        "Citation grounding rate: 100% on accepted answers",
        "Failure mode shifted from 'confidently wrong' to 'politely refused' — the right tradeoff",
        "Latency overhead: ~280 ms per query from the verifier (acceptable for the safety win)",
      ],
      lessons:
        "Trust is built with a measurement loop, not better prompts. The system improved fastest when every component had its own observable metric and could be benchmarked in isolation.",
      whatNext:
        "Replace the NLI model with a fine-tuned domain verifier and add a learned retrieval policy that picks BM25/dense weights per-query.",
    },
  },
  {
    id: "healthcare-cyber",
    index: "03",
    category: "ML · Healthcare Cybersecurity",
    accent: "coral",
    title: "Ensemble Anomaly Detection for Healthcare Cybersecurity",
    description:
      "An ensemble ML system that detects malicious activity in healthcare network telemetry by combining stacking and voting classifiers — engineered to lift recall and crush false positives that plague single-model detectors.",
    highlights: [
      "Stacking + voting ensembles",
      "Feature engineering pipeline",
      "Cross-validation workflows",
      "Cybersecurity-focused evaluation",
    ],
    metrics: [
      { label: "Detection", value: "High", caption: "accuracy on eval set" },
      { label: "False Positives", value: "↓ Reduced", caption: "vs. single model" },
      { label: "Models", value: "Stack+Vote", caption: "ensemble strategy" },
      { label: "Pipelines", value: "Scalable", caption: "preprocessing" },
    ],
    stack: ["Python", "Scikit-learn", "Pandas", "NumPy", "Machine Learning", "Cybersecurity"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/567d72b0-6a32-4256-8c91-8278a4e7aa73/images/b9a67196487b7212018fa24bb8c568d2ade78999fa7893b16d2a95dc82d63851.png",
    github: "https://github.com/Meghashyam-adimallam/ML_HealthCare_CyberSecurity_Project",
    caseStudy: {
      tagline:
        "Healthcare networks fail loudly when intrusion detection cries wolf. An ensemble that hears the signal — and ignores the noise.",
      role: "Sole engineer · 5 weeks",
      year: "2024",
      problem:
        "Single-model intrusion detectors are either too aggressive (alert fatigue, ignored warnings) or too quiet (missed attacks). Healthcare systems can't afford either. The goal: a measurably better detector by combining models that fail differently.",
      architecture: [
        { node: "Telemetry", note: "Network / system features" },
        { node: "Preprocess", note: "Cleaning + feature engineering" },
        { node: "Base Models", note: "Diverse classifiers (RF, GB, LR, SVM)" },
        { node: "Voting", note: "Soft voting ensemble" },
        { node: "Stacking", note: "Meta-learner over base models" },
        { node: "Eval", note: "K-fold CV + cybersecurity metrics" },
      ],
      decisions: [
        {
          title: "Diverse base learners",
          body: "Ensembles only win when components fail differently. I picked tree-based (Random Forest, Gradient Boosting), linear (Logistic Regression), and margin-based (SVM) models so their error patterns don't correlate.",
        },
        {
          title: "Stacking over straight averaging",
          body: "A meta-learner can weight base models per-sample based on the input distribution. On imbalanced cybersecurity data, this consistently beat simple voting in cross-validation.",
        },
        {
          title: "Cybersecurity-aware evaluation",
          body: "Accuracy is misleading on imbalanced threat data. I optimized for recall on the malicious class while monitoring false-positive rate — the metric pair that matters operationally.",
        },
        {
          title: "Reproducible CV protocol",
          body: "All numbers come from K-fold cross-validation with the same splits across models, so improvements aren't lucky fold noise.",
        },
      ],
      results: [
        "Higher detection accuracy than the best single base model",
        "Materially fewer false positives vs. baseline ensembles",
        "Robust performance across cross-validation folds (low variance)",
        "Reusable preprocessing + evaluation harness for future telemetry datasets",
      ],
      lessons:
        "On imbalanced security data, the modeling choices matter less than the evaluation protocol. Once the harness was honest, the right architecture became obvious.",
      whatNext:
        "Add a streaming inference layer and integrate with a SIEM so alerts land where defenders already live.",
    },
  },
  {
    id: "qa",
    index: "04",
    category: "RAG · Research Tooling",
    accent: "amber",
    title: "Research Paper Q&A Bot",
    description:
      "Interactive research assistant that answers contextual questions from uploaded papers. PDF ingestion, semantic retrieval, conversational memory, FAISS vector search, and local LLM inference.",
    highlights: [
      "PDF ingestion",
      "Semantic retrieval",
      "Conversational memory",
      "Local LLM inference",
    ],
    metrics: [
      { label: "Embedding", value: "FAISS", caption: "vector index" },
      { label: "Runtime", value: "Local", caption: "via Ollama" },
      { label: "UX", value: "Streamlit", caption: "live UI" },
    ],
    stack: ["LangChain", "Streamlit", "FAISS", "Hugging Face", "Ollama"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/567d72b0-6a32-4256-8c91-8278a4e7aa73/images/b9a67196487b7212018fa24bb8c568d2ade78999fa7893b16d2a95dc82d63851.png",
    github: "https://github.com/Meghashyam-adimallam/Research-paper-qa-bot",
    caseStudy: {
      tagline:
        "A research companion that reads with you — local-first, semantic, and answer-grounded in the actual paper.",
      role: "Sole engineer · 3 weeks",
      year: "2024",
      problem:
        "Skimming PDFs is slow; ChatGPT doesn't see your paper. The goal was a focused tool that reads a single paper, holds the context for an entire session, and answers from the paper — not from the LLM's training data.",
      architecture: [
        { node: "PDF Upload", note: "Streamlit drag-drop" },
        { node: "Parser", note: "Chunk + clean text" },
        { node: "Embedder", note: "Hugging Face models" },
        { node: "FAISS Index", note: "Vector search" },
        { node: "Local LLM", note: "Ollama runtime" },
        { node: "Chat", note: "Memory-aware Q&A" },
      ],
      decisions: [
        {
          title: "Local-first inference via Ollama",
          body: "Researchers care about privacy. Running the LLM locally (no API key, no cloud round-trip) made the tool usable for unpublished or sensitive papers and removed cost-per-query as a friction point.",
        },
        {
          title: "Section-aware chunking",
          body: "Naïve fixed-size chunks straddle figure captions and equations. A section-aware splitter (using PDF outline + heuristic) kept logical units intact — retrieval quality jumped noticeably on equation-heavy ML papers.",
        },
        {
          title: "Conversational memory with rolling context",
          body: "A session-level memory window lets follow-up questions resolve ('why does that hold?') without forcing the user to repeat the topic, while keeping the prompt budget tight.",
        },
        {
          title: "Streamlit for the UI",
          body: "For a research-tool MVP, Streamlit was the fastest path to a usable app. The chat surface, upload, and citation panel all fit cleanly in a single file — no framework tax.",
        },
      ],
      results: [
        "Working end-to-end QA from PDF upload to answer in under 60s on commodity hardware",
        "Zero external API calls — fully local LLM inference",
        "Used during my own coursework as a daily reading companion",
      ],
      lessons:
        "Scope is the feature. A focused tool that does one thing well (paper Q&A) is more useful than a general assistant for the same task.",
      whatNext:
        "Add multi-paper cross-referencing and a citation-graph view so the tool can answer questions that span related work.",
    },
  },
];

export const techStack = [
  {
    category: "AI / LLM Systems",
    accent: "cyan",
    items: [
      "LLMs", "RAG", "LangChain", "LangGraph", "Prompt Engineering",
      "vLLM", "TensorRT-LLM", "Hugging Face", "AI Agents",
    ],
  },
  {
    category: "Machine Learning",
    accent: "teal",
    items: [
      "PyTorch", "Scikit-learn", "NLP", "Computer Vision",
      "BERT", "YOLO", "Model Optimization",
    ],
  },
  {
    category: "Data Science & Analytics",
    accent: "coral",
    items: [
      "NumPy", "Pandas", "Matplotlib", "Scikit-Learn", "Power BI", "Tableau",
    ],
  },
  {
    category: "Backend & Deployment",
    accent: "amber",
    items: [
      "FastAPI", "Flask", "Docker", "Kubernetes",
      "REST APIs", "AWS", "GCP", "CI/CD",
    ],
  },
  {
    category: "Data & Infrastructure",
    accent: "pink",
    items: [
      "Python", "SQL", "Kafka", "ETL", "CUDA", "Git",
    ],
  },
  {
    category: "Tooling & Collaboration",
    accent: "sky",
    items: [
      "Jupyter", "VS Code", "GitHub Actions", "MLflow", "Weights & Biases",
    ],
  },
];

export const certifications = [
  {
    name: "AWS Certified Cloud Practitioner (CCP)",
    issuer: "AWS · Credly",
    year: "2025",
    accent: "amber",
    credentialUrl:
      "https://www.credly.com/badges/f97c5342-f8b2-401f-b7ed-59783bdd1262/linked_in?t=swtolx",
  },
  {
    name: "IBM Generative AI Professional Certificate",
    issuer: "Coursera · IBM",
    year: "2025",
    accent: "cyan",
    credentialUrl:
      "https://www.coursera.org/account/accomplishments/professional-cert/8S5WPH18L3HY",
  },
  {
    name: "IBM Data Science Professional Certificate",
    issuer: "Coursera · IBM",
    year: "2025",
    accent: "teal",
    credentialUrl:
      "https://www.coursera.org/account/accomplishments/professional-cert/5NU6G15LLFSO",
  },
  {
    name: "Deep Learning Specialization",
    issuer: "Packt",
    year: "2025",
    accent: "coral",
    credentialUrl: null,
  },
];

export const education = [
  {
    school: "University of Alabama at Birmingham (UAB)",
    degree: "Master of Science in Data Science",
    period: "August 2024 — May 2026",
    location: "Birmingham, Alabama, USA",
    gpa: "4.0 / 4.0",
    status: "Graduated",
    accent: "cyan",
    focus: [
      "Machine Learning & Deep Learning",
      "Natural Language Processing",
      "Data Engineering & MLOps",
      "Applied Statistics & Optimization",
    ],
  },
  {
    school: "BV Raju Institute of Technology",
    degree: "Bachelor of Technology (B.Tech)",
    specialization: "Computer Science and Business Systems",
    period: "2020 — 2024",
    location: "India",
    status: "Graduated",
    accent: "teal",
    focus: [
      "Computer Science Fundamentals",
      "Business Systems & Analytics",
      "Software Engineering",
      "Data Structures & Algorithms",
    ],
  },
];

export const tickerWords = [
  "RAG", "vLLM", "TensorRT-LLM", "LangGraph", "FastAPI",
  "PyTorch", "CUDA", "INT8", "FP16", "KV-Cache",
  "BM25", "FAISS", "Embeddings", "Agents", "MLOps",
  "Kubernetes", "AWS", "Hugging Face", "BERT", "Quantization",
];

// Rebalanced accent palette — cyan / teal / sky / coral / amber / pink
// (Reduced violet dominance per user direction.)
export const accentTokens = {
  cyan:   { text: "text-cyan-300",    ring: "from-cyan-400/40 via-sky-400/30 to-teal-400/40",     blob: "bg-cyan-400/30",  chip: "text-cyan-200 border-cyan-400/30 bg-cyan-500/10",   glow: "shadow-[0_0_40px_-10px_rgba(34,211,238,0.55)]" },
  teal:   { text: "text-teal-300",    ring: "from-teal-400/40 via-emerald-400/30 to-sky-400/40",  blob: "bg-teal-400/30",  chip: "text-teal-200 border-teal-400/30 bg-teal-500/10",   glow: "shadow-[0_0_40px_-10px_rgba(45,212,191,0.55)]" },
  sky:    { text: "text-sky-300",     ring: "from-sky-400/40 via-blue-400/30 to-cyan-400/40",     blob: "bg-sky-400/30",   chip: "text-sky-200 border-sky-400/30 bg-sky-500/10",      glow: "shadow-[0_0_40px_-10px_rgba(56,189,248,0.55)]" },
  coral:  { text: "text-rose-300",    ring: "from-rose-400/40 via-orange-400/30 to-amber-400/40", blob: "bg-rose-400/30",  chip: "text-rose-200 border-rose-400/30 bg-rose-500/10",   glow: "shadow-[0_0_40px_-10px_rgba(251,113,133,0.55)]" },
  amber:  { text: "text-amber-300",   ring: "from-amber-400/40 via-orange-400/30 to-rose-400/40", blob: "bg-amber-400/30", chip: "text-amber-200 border-amber-400/30 bg-amber-500/10", glow: "shadow-[0_0_40px_-10px_rgba(251,191,36,0.55)]" },
  pink:   { text: "text-pink-300",    ring: "from-pink-400/40 via-rose-400/30 to-fuchsia-400/40", blob: "bg-pink-400/30",  chip: "text-pink-200 border-pink-400/30 bg-pink-500/10",   glow: "shadow-[0_0_40px_-10px_rgba(244,114,182,0.55)]" },
};
